// Estimate Engine ▸ Items — paste-from-Excel importer (pure logic; the panel
// is ItemImportPanel in src/components/). Seeds the global catalog
// (EstimateCostItems, db/30) from a previous estimate's worksheet.
//
// Product rules (Ben Porter, 2026-09-24):
//   • COST ONLY. Sales burden and markup are pricing levers chosen per
//     estimate / per strategic opportunity — never imported into the catalog.
//     Every imported row is saved with SalesBurdenPct / MarkupPct BLANK
//     (NULL, never 0: 0 opts the row out of the estimate's own markup settings
//     and sells it at cost — see computeUnitSellPrice in estimatePricing.js),
//     and a matched existing row has any burden / markup it carries cleared.
//   • Imported columns: Cost Code, Description, UOM, Subcontract (vendor/sub)
//     unit cost, optional Material unit cost / Labor hr per unit / Code, and
//     the Division from a group header row ("Civil") or a Division column.
//     Qty, the totals, Unit Cost (= sell per unit), Cost/SF, Zone, Sales
//     Burden and Markup are recognised and deliberately ignored.
//   • Cost codes are corrected on the sheet before import. A code that is not
//     in CostCodes imports with NO cost code (it lands in the Items tab's
//     "No Cost Code" group for a by-hand fix) — a matched existing row keeps
//     the code it has. Never guessed.
//   • A row matches an existing ACTIVE item by Code when the sheet has one,
//     else by cleaned item name (cost code breaks a tie). A match is updated;
//     no match is created; an ambiguous match is skipped and listed. Nothing
//     is ever deleted.
import { splitDelimited } from "./scheduleHistory";
import { normalizeUomCode } from "./uoms";

// Column vocabulary. `key: null` = recognised and ignored on purpose.
export const IMPORT_COLUMNS = [
  { key: "code",          aliases: ["code", "item code"] },
  { key: "cost_code",     aliases: ["cost code", "csi code", "csi", "costcode"] },
  { key: "description",   aliases: ["description", "item name", "item", "name"] },
  { key: "uom",           aliases: ["uom", "unit", "units", "u/m"] },
  { key: "vendor",        aliases: ["subcontract unit cost", "sub unit cost", "subcontractor unit cost", "vendor/sub $/unit", "vendor sub unit cost", "vendor unit cost", "vendor/sub unit cost"] },
  { key: "material",      aliases: ["material unit cost", "material $/unit", "material cost/unit", "material cost per unit", "material cost"] },
  { key: "labor",         aliases: ["labor hr/unit", "labor hours/unit", "labor hr per unit", "labor hrs/unit", "labor hours per unit"] },
  { key: "division",      aliases: ["division"] },
  { key: null, ignoredAs: "pricing lever — set per estimate", aliases: ["sales burden", "sales burden %", "markup", "markup %", "mark up"] },
  { key: null, ignoredAs: "project-specific", aliases: ["qty", "quantity", "zone", "cell #", "cell", "total subcontract cost", "total cost", "total value", "unit cost", "cost/sf", "cost / sf", "total material cost", "total labor"] },
];
const norm = (s) => String(s ?? "").replace(/^﻿/, "").trim().toLowerCase().replace(/\s+/g, " ");
const COLUMN_BY_ALIAS = new Map(IMPORT_COLUMNS.flatMap(c => c.aliases.map(a => [a, c])));

// "$1,000.00" → 1000 · "$-" / "" → null · "(5.00)" → -5 · "#DIV/0!" → NaN
export function parseMoney(v) {
  if (v == null) return null;
  let s = String(v).trim();
  if (s === "") return null;
  let neg = false;
  if (/^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1); }
  s = s.replace(/[$,\s]/g, "");
  if (s === "" || s === "-") return null;
  const n = +s;
  if (!Number.isFinite(n)) return NaN;
  return neg ? -n : n;
}

// Stray leading apostrophes / quotes are Excel paste artifacts ('Lockers).
export const cleanItemName = (s) => String(s ?? "").replace(/^[\s'"`‘’]+/, "").replace(/\s+/g, " ").trim();
export const nameKey = (s) => cleanItemName(s).toLowerCase();
const digitsOf = (s) => String(s ?? "").replace(/\D/g, "");

// Accepts "31 00 00", "310000", "31-00-00", "31.00.00" — digits must equal a
// cost code's CSICodeNumeric (or its CSICode's digits). No padding or
// truncation: "3100" is not assumed to mean 31 00 00.
export function resolveCostCode(raw, costCodes = []) {
  const d = digitsOf(raw);
  if (!d) return null;
  return costCodes.find(c => digitsOf(c.CSICodeNumeric) === d || digitsOf(c.CSICode) === d) || null;
}

export function resolveUom(raw, uoms = []) {
  const code = normalizeUomCode(raw);
  if (!code) return null;
  const active = uoms.filter(u => u.IsActive !== false && u.IsActive !== 0);
  const hit = active.find(u => normalizeUomCode(u.Code) === code) || active.find(u => normalizeUomCode(u.Name) === code);
  return hit ? normalizeUomCode(hit.Code) : null;
}

export const resolveDivision = (raw, divisions = []) => {
  const k = norm(raw);
  return k ? divisions.find(d => norm(d.Name) === k || norm(d.Code) === k) || null : null;
};

// text → { header: { mapped, ignored, unmapped }, rows: [{ line, cells: {key: raw}, texts: [non-empty raw cells] }], errors }
export function parseItemSheet(text) {
  const lines = String(text ?? "").replace(/\r\n?/g, "\n").split("\n");
  const empty = { header: { mapped: [], ignored: [], unmapped: [] }, rows: [], errors: [] };
  const first = lines.findIndex(l => l.trim() !== "");
  if (first < 0) return { ...empty, errors: [{ line: 0, message: "Paste the sheet with its header row." }] };
  const delim = lines.some(l => l.includes("\t")) ? "\t" : ","; // Excel copies tab-separated; a CSV file has none
  // The header is the first line naming a Description column (a title row
  // above it, e.g. the project name, is tolerated).
  let headerIdx = lines.findIndex(l => splitDelimited(l, delim).some(h => COLUMN_BY_ALIAS.get(norm(h))?.key === "description"));
  if (headerIdx < 0) headerIdx = first;
  const headerLine = lines[headerIdx];
  const headers = splitDelimited(headerLine, delim);
  const mapped = [], ignored = [], unmapped = [];
  const keyAt = headers.map(h => {
    const k = norm(h);
    if (!k) return null;
    const col = COLUMN_BY_ALIAS.get(k);
    if (!col) { unmapped.push(h.trim()); return null; }
    if (!col.key) { ignored.push({ header: h.trim(), reason: col.ignoredAs }); return null; }
    if (mapped.some(m => m.key === col.key)) { ignored.push({ header: h.trim(), reason: `second "${col.key}" column` }); return null; }
    mapped.push({ header: h.trim(), key: col.key });
    return col.key;
  });
  const errors = [];
  if (!keyAt.includes("description")) errors.push({ line: headerIdx + 1, message: "No Description column found in the header row." });
  if (!keyAt.includes("uom")) errors.push({ line: headerIdx + 1, message: "No UOM column found in the header row." });
  if (!keyAt.some(k => k === "vendor" || k === "material" || k === "labor")) errors.push({ line: headerIdx + 1, message: "No cost column found (Subcontract Unit Cost, Material Unit Cost or Labor Hr/Unit)." });
  const rows = [];
  if (!errors.length) {
    for (let i = headerIdx + 1; i < lines.length; i++) {
      if (lines[i].trim() === "") continue;
      const raw = splitDelimited(lines[i], delim);
      const cells = {};
      keyAt.forEach((k, c) => { if (k) cells[k] = (raw[c] ?? "").trim(); });
      rows.push({ line: i + 1, cells, texts: raw.map(s => s.trim()).filter(Boolean) });
    }
  }
  return { header: { mapped, ignored, unmapped }, rows, errors };
}

// Row → one of: header (division / group), blank, error, item.
function classifyRow(r, ctx) {
  const c = r.cells;
  const name = cleanItemName(c.description);
  const costs = { vendor: parseMoney(c.vendor), material: parseMoney(c.material), labor: parseMoney(c.labor) };
  const anyCost = Object.values(costs).some(v => v != null && !Number.isNaN(v) && v !== 0);
  if (!name) {
    // A lone label like "Civil" (in whichever column) is a division header;
    // numbers, currency, percentages and Excel errors (#DIV/0!) are not labels.
    const labels = r.texts.filter(t => !/^[\d\s.$,()%-]+$/.test(t) && !/^#/.test(t));
    const div = labels.length === 1 ? resolveDivision(labels[0], ctx.divisions) : null;
    if (div) return { kind: "division", division: div };
    if (labels.length && !anyCost) return { kind: "header", label: labels.join(" ") };
    return { kind: "blank" };
  }
  if (!c.uom && !anyCost) {
    const div = resolveDivision(name, ctx.divisions); // "Civil" typed in the Description column
    return div ? { kind: "division", division: div } : { kind: "header", label: name };
  }
  return { kind: "item", name, costs };
}

const sameNum = (a, b) => (a == null || a === "" ? null : +a) === (b == null || b === "" ? null : +b);

// Parsed sheet + catalog → the preview plan. Every row the estimator pasted
// comes back with an action and its reasons; nothing is dropped silently.
//   action: create | update | unchanged | skip | error
export function buildImportPlan(parsed, { items = [], costCodes = [], uoms = [], divisions = [] } = {}) {
  const ctx = { divisions };
  const active = items.filter(i => i.IsActive !== false && i.IsActive !== 0);
  const byCode = new Map(), byName = new Map();
  for (const i of active) {
    if (i.Code) { const k = norm(i.Code); byCode.set(k, [...(byCode.get(k) || []), i]); }
    const k = nameKey(i.ItemName);
    byName.set(k, [...(byName.get(k) || []), i]);
  }
  const hasCol = (k) => parsed.header.mapped.some(m => m.key === k);
  const out = [];
  const seen = new Map(); // import key → first plan row
  let division = null;
  for (const r of parsed.rows) {
    const cls = classifyRow(r, ctx);
    if (cls.kind === "division") { division = cls.division; out.push({ line: r.line, action: "header", label: `Division: ${cls.division.Name}` }); continue; }
    if (cls.kind === "header") { out.push({ line: r.line, action: "header", label: cls.label }); continue; }
    if (cls.kind === "blank") { out.push({ line: r.line, action: "blank" }); continue; }

    const c = r.cells;
    const warnings = [], errors = [];
    const uom = resolveUom(c.uom, uoms);
    if (!c.uom) errors.push("no UOM");
    else if (!uom) errors.push(`UOM "${c.uom}" is not in Units of Measure — add it there or fix the sheet`);
    for (const [k, v] of Object.entries(cls.costs)) if (Number.isNaN(v)) errors.push(`"${c[k]}" is not a number (${k})`);
    for (const [k, v] of Object.entries(cls.costs)) if (v != null && v < 0) errors.push(`negative ${k} cost`);
    const cc = resolveCostCode(c.cost_code, costCodes);
    if (!c.cost_code) warnings.push("no cost code on the sheet");
    else if (!cc) warnings.push(`cost code "${c.cost_code}" is not in Cost Codes`);
    let div = division;
    if (hasCol("division") && c.division) { div = resolveDivision(c.division, divisions); if (!div) warnings.push(`division "${c.division}" not found`); }
    if (!Object.values(cls.costs).some(v => v != null && !Number.isNaN(v) && v > 0)) warnings.push("no cost on the sheet — imports at $0");

    const base = { line: r.line, name: cls.name, code: c.code || null, uom, costCode: cc, division: div, costs: cls.costs, warnings, errors };
    if (errors.length) { out.push({ ...base, action: "error" }); continue; }

    const importKey = c.code ? `code:${norm(c.code)}` : `name:${nameKey(cls.name)}`;
    const prior = seen.get(importKey);
    if (prior) {
      const same = prior.uom === uom && ["vendor", "material", "labor"].every(k => sameNum(prior.costs[k], cls.costs[k]));
      out.push({ ...base, action: "skip", reason: same ? `repeat of line ${prior.line}` : `same item as line ${prior.line} with a different cost/UOM — only line ${prior.line} is imported` });
      continue;
    }

    let candidates = c.code ? byCode.get(norm(c.code)) || [] : [];
    if (!candidates.length) candidates = byName.get(nameKey(cls.name)) || [];
    if (candidates.length > 1 && cc) {
      const narrowed = candidates.filter(i => i.CostCodeID === cc.CostCodeID);
      if (narrowed.length) candidates = narrowed;
    }
    if (candidates.length > 1) {
      out.push({ ...base, action: "skip", reason: `${candidates.length} existing items share this name — fix by hand (IDs ${candidates.map(i => i.ItemID).join(", ")})` });
      continue;
    }
    const existing = candidates[0] || null;
    const next = importedFields(base, existing, { hasMaterial: hasCol("material"), hasLabor: hasCol("labor") });
    const plan = { ...base, existing, next, changes: existing ? diffItem(existing, next) : [] };
    plan.action = !existing ? "create" : plan.changes.length ? "update" : "unchanged";
    seen.set(importKey, plan);
    out.push(plan);
  }
  return out;
}

// The fields an import writes. A column the sheet does not have is BLANK on
// a new item and CLEARED on a matched one (a sell price typed into Material
// must not survive next to the imported sub cost), except the cost code and
// division, which keep the existing value when the sheet has none that resolves.
function importedFields(base, existing, { hasMaterial, hasLabor }) {
  const cost = (v) => (v == null || Number.isNaN(v) ? null : v);
  return {
    ItemName: base.name,
    Code: base.code ?? existing?.Code ?? null,
    UOM: base.uom,
    CostCodeID: base.costCode?.CostCodeID ?? existing?.CostCodeID ?? null,
    DivisionID: base.division?.CategoryLibraryID ?? existing?.DivisionID ?? null,
    VendorSubCostPerUnit: cost(base.costs.vendor) ?? 0,
    MaterialCostPerUnit: hasMaterial ? cost(base.costs.material) : null,
    LaborHrPerUnit: hasLabor ? cost(base.costs.labor) : null,
    SalesBurdenPct: null,
    MarkupPct: null,
  };
}

const FIELD_LABELS = {
  ItemName: "Name", Code: "Code", UOM: "UOM", CostCodeID: "Cost code", DivisionID: "Division",
  VendorSubCostPerUnit: "Vendor/sub $", MaterialCostPerUnit: "Material $", LaborHrPerUnit: "Labor hr",
  SalesBurdenPct: "Sales burden %", MarkupPct: "Markup %",
};
const NUMERIC = new Set(["VendorSubCostPerUnit", "MaterialCostPerUnit", "LaborHrPerUnit", "SalesBurdenPct", "MarkupPct"]);
export function diffItem(existing, next) {
  const changes = [];
  for (const [field, to] of Object.entries(next)) {
    const from = existing[field] ?? null;
    const equal = NUMERIC.has(field)
      ? (from == null ? null : +from) === (to == null ? null : +to)
      : String(from ?? "") === String(to ?? "");
    if (!equal) changes.push({ field, label: FIELD_LABELS[field] || field, from, to });
  }
  return changes;
}

// The form-shaped row handed to the Items tab's own save payload builder
// (itemSavePayload in costLibraryPayload.js) — an update starts from the full
// existing row because sp_SaveEstimateCostItem replaces every column.
export function planRowToItem(plan, emptyItem) {
  if (plan.existing) return { ...plan.existing, ...plan.next };
  return { ...emptyItem, ...plan.next, Trade: "general" };
}

export function summarizePlan(plan) {
  const s = { create: 0, update: 0, unchanged: 0, skip: 0, error: 0, header: 0, blank: 0, warnings: 0, clearsLevers: 0, noCostCode: 0 };
  for (const p of plan) {
    s[p.action] = (s[p.action] || 0) + 1;
    if (p.warnings?.length && (p.action === "create" || p.action === "update")) s.warnings++;
    if (p.action === "update" && p.changes.some(ch => ch.field === "SalesBurdenPct" || ch.field === "MarkupPct")) s.clearsLevers++;
    if ((p.action === "create" || p.action === "update") && p.next.CostCodeID == null) s.noCostCode++;
  }
  return s;
}
